import debounce from 'lodash.debounce';
import { createContext, ReactElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useDebugLogging } from '../utils';

type KeepAlive = () => void;
type TimeoutContextValue = {
  keepAlive: KeepAlive;
  isAlive: boolean;
  setTrackingEnabled: (val: boolean) => void;
};

const TimeoutContext = createContext<TimeoutContextValue>({
  keepAlive: () => undefined,
  isAlive: true,
  setTrackingEnabled: () => undefined,
});

const DEBOUNCE_INTERVAL = 2000;
const SECONDS_PER_MINUTE = 60;
const MILLIS_PER_SECOND = 1000;
const DEFAULT_INACTIVITY_IN_MINUTES = 15;

const userEvents = ['mousemove', 'keydown', 'click', 'touchmove'];

const useUserActivityTracker = (keepAlive: KeepAlive, trackingActive: boolean) => {
  const mounted = useRef(false);

  useEffect(() => {
    const isMounted = mounted.current;
    if (trackingActive && !isMounted) {
      mounted.current = true;
      for (const e of userEvents) {
        globalThis.addEventListener(e, keepAlive);
      }
    }
    const removeEventListeners = () => {
      mounted.current = false;
      for (const e of userEvents) {
        globalThis.removeEventListener(e, keepAlive);
      }
    };
    if (!trackingActive && isMounted) {
      removeEventListeners();
    }
    return removeEventListeners;
  }, [keepAlive, trackingActive]);
};

const useKeepAliveFunction = (onTimeout: () => void, trackingEnabled: boolean, userTimeoutInMinutes: number) => {
  const debugLog = useDebugLogging();
  const sessionTimeoutInMillis = userTimeoutInMinutes * MILLIS_PER_SECOND * SECONDS_PER_MINUTE;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const resetTimeout = useCallback(() => {
    const ref = timeoutRef.current;
    const cancelTimeout = () => {
      clearTimeout(ref);
    };
    if (ref) cancelTimeout();

    if (trackingEnabled) {
      debugLog('keep alive');
      timeoutRef.current = setTimeout(onTimeout, sessionTimeoutInMillis);
    }
    return cancelTimeout;
  }, [onTimeout, trackingEnabled, sessionTimeoutInMillis, debugLog]);

  useEffect(() => {
    const ref = timeoutRef.current;
    resetTimeout();
    return () => {
      if (ref) clearTimeout(ref);
    };
  }, [resetTimeout]);

  const keepAlive = useMemo(
    () => debounce(resetTimeout, DEBOUNCE_INTERVAL, { leading: true, maxWait: DEBOUNCE_INTERVAL }),
    [resetTimeout]
  );
  return keepAlive;
};

const TimeoutProvider = ({
  children,
  userTimeoutInMinutes = DEFAULT_INACTIVITY_IN_MINUTES,
}: {
  readonly children: ReactElement;
  readonly userTimeoutInMinutes?: number;
}) => {
  const addToLog = useDebugLogging();
  const [isAlive, setIsAlive] = useState(true);
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const onTimeout = useCallback(() => {
    addToLog('activity timeout');
    setIsAlive(false);
  }, [addToLog]);
  const keepAlive = useKeepAliveFunction(onTimeout, trackingEnabled, userTimeoutInMinutes);
  useUserActivityTracker(keepAlive, trackingEnabled);

  const value = useMemo(
    () => ({
      isAlive,
      keepAlive,
      setTrackingEnabled,
    }),
    [isAlive, keepAlive, setTrackingEnabled]
  );
  return <TimeoutContext.Provider value={value}>{children}</TimeoutContext.Provider>;
};

const useTimeoutContext = () => useContext(TimeoutContext);

const useKeepAlive = () => {
  const { keepAlive } = useTimeoutContext();
  useEffect(() => keepAlive(), [keepAlive]);

  return keepAlive;
};
const useIsAlive = () => useTimeoutContext().isAlive;

export type { KeepAlive };

export { TimeoutProvider, useIsAlive, useKeepAlive, useTimeoutContext };
